import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Check, Download, ExternalLink, LayoutDashboard, MessageSquare, Phone, Users, Wallet, Calendar, Bell, Mail } from "lucide-react"
import { BreadcrumbItem } from '@/components/ui/breadcrumb-nav'
import { useDarDenContext } from './DarDenAppsPage'

export function TratoPage() {
  // Get breadcrumbs from parent context
  let breadcrumbs: BreadcrumbItem[] = []
  try {
    const { parentBreadcrumbs } = useDarDenContext()
    breadcrumbs = [
      ...parentBreadcrumbs,
      {
        label: "Trato",
        description: "The freelancer toolbox packaged to harness the power of AI",
        href: "/software/darden/trato"
      }
    ]
  } catch (e) {
    // Handle case where context might not be available
    console.log("Context not available, rendering standalone page")
  }

  return (
    <div>
      {/* Main content */}
      <section className="flex flex-col-reverse md:flex-row gap-8 md:gap-12 py-8 md:py-16">
        <div className="flex-1 space-y-6">
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-bold tracking-tighter">Trato</h1>
            <p className="text-xl md:text-2xl text-neutral-600 dark:text-neutral-400">
              The freelancer's all-in-one business solution
            </p>
          </div>

          <p className="text-lg text-neutral-600 dark:text-neutral-400">
            Get more clients, reduce taxes and accounting costs, save time, and finally enable benefits & retirement as a solo freelancer.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button className="font-medium" size="lg" asChild>
              <a href="https://dardenapps.com/trato" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" /> Navigate to Trato's Website
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href="mailto:hello+support.trato@dardenapp.com">
                <Mail className="mr-2 h-4 w-4" /> Contact Support
              </a>
            </Button>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center">
              <Check className="mr-1 h-4 w-4 text-green-500" />
              <span>Client management</span>
            </div>
            <div className="flex items-center">
              <Check className="mr-1 h-4 w-4 text-green-500" />
              <span>International payments</span>
            </div>
            <div className="flex items-center">
              <Check className="mr-1 h-4 w-4 text-green-500" />
              <span>Retirement planning</span>
            </div>
          </div>
        </div>

        <div className="flex-1 relative rounded-lg overflow-hidden shadow-xl bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-800 min-h-[300px] md:min-h-[400px]">
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="https://res.cloudinary.com/dn50pctna/image/upload/fl_preserve_transparency/v1721159473/DarDen/logo_trato_by_darden_gviaur.jpg?_s=public-apps"
              alt="Trato logo"
              className="w-auto h-auto max-w-full max-h-full object-contain p-8"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tight">Key Benefits</h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
            Discover how Trato empowers freelancers with essential business tools
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => (
            <Card key={feature.title} className="transition-all hover:shadow-md">
              <CardHeader>
                <feature.icon className="h-10 w-10 text-blue-600 dark:text-blue-400 mb-3" />
                <CardTitle>{feature.title}</CardTitle>
                <CardDescription>{feature.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 space-y-8">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tight">How Trato Works</h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
            A comprehensive solution for every aspect of your freelance business
          </p>
        </div>

        <Tabs defaultValue="clients" className="w-full">
          <TabsList className="mx-auto flex flex-wrap justify-center gap-2 mb-8 p-2 sticky top-0 bg-background z-10">
            <TabsTrigger value="clients" className="px-3 py-2 text-sm">Get More Clients</TabsTrigger>
            <TabsTrigger value="taxes" className="px-3 py-2 text-sm">Cut on Taxes</TabsTrigger>
            <TabsTrigger value="time" className="px-3 py-2 text-sm">Save Time</TabsTrigger>
            <TabsTrigger value="benefits" className="px-3 py-2 text-sm">Retirement & Benefits</TabsTrigger>
            <TabsTrigger value="ai" className="px-3 py-2 text-sm">AI Features</TabsTrigger>
          </TabsList>

          {tabContent.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="space-y-6 mt-4">
              <div className="flex flex-col md:flex-row gap-0 items-start">
                <div className="flex-1 space-y-4 order-2 md:order-1">
                  <h3 className="text-2xl font-bold">{tab.title}</h3>
                  <p className="text-lg text-neutral-600 dark:text-neutral-400">{tab.description}</p>
                  <ul className="space-y-2">
                    {tab.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 relative rounded-lg overflow-hidden shadow-xl bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 min-h-[200px] md:min-h-[350px] order-1 md:order-2 mb-6 md:mb-0">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <tab.icon className="h-16 w-16 md:h-20 md:w-20 text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Pricing Section */}
      <section className="py-16 space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tight">Pricing Plans</h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
            Choose the plan that fits your freelance business needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan) => (
            <Card key={plan.name} className={`transition-all hover:shadow-lg ${plan.popular ? 'border-2 border-blue-500 dark:border-blue-400' : ''}`}>
              <CardHeader>
                <div className="space-y-2">
                  {plan.popular && (
                    <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-medium">
                      Most Popular
                    </span>
                  )}
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="flex items-end">
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-neutral-500 dark:text-neutral-400 ml-1">/mo</span>
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`} asChild>
                  <a href="https://dardenapps.com/trato" target="_blank" rel="noopener noreferrer">
                    Get Started
                  </a>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
          All plans include a 3-week money-back guarantee
        </p>
      </section>

      {/* Testimonials */}
      <section className="py-16 space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tight">Trusted by Freelancers</h2>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
            See what our users have to say about Trato
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, i) => (
            <Card key={i} className="bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <p className="text-lg italic">"{testimonial.quote}"</p>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{testimonial.name}</p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">{testimonial.title}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <Card className="w-full bg-gradient-to-r from-blue-600 to-purple-700 text-white">
          <CardContent className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center md:justify-between gap-8">
              <div className="space-y-4 text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold">Ready to grow your freelance business?</h3>
                <p className="text-blue-100">Join thousands of freelancers using Trato to streamline their workflows.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-white text-blue-600 hover:bg-blue-50" size="lg" asChild>
                  <a href="https://dardenapps.com/trato" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" /> Visit Trato
                  </a>
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white/10" size="lg" asChild>
                  <a href="mailto:hello+support.trato@dardenapp.com">
                    <Mail className="mr-2 h-4 w-4" /> Contact Support
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

// Data for features section
const features = [
  {
    title: "Public Profile & Links",
    description: "Create a professional portfolio and custom links to initiate client conversations.",
    icon: Users
  },
  {
    title: "Client Portal",
    description: "Give clients a premium onboarding and offboarding experience with dedicated portals.",
    icon: LayoutDashboard
  },
  {
    title: "International Payments",
    description: "Receive payments in USD from anywhere using integrated FedNow & USDC solutions.",
    icon: Wallet
  },
  {
    title: "Automated Pipeline",
    description: "Keep track of leads, automate responses, scheduling, and follow-ups.",
    icon: LayoutDashboard
  },
  {
    title: "Financial Goals",
    description: "Allocate contributions from your income toward retirement, savings, and benefits.",
    icon: Wallet
  },
  {
    title: "AI Assistants",
    description: "Leverage custom AI assistants and automated workflows to streamline your business.",
    icon: MessageSquare
  }
]

// Data for tabs section
const tabContent = [
  {
    value: "clients",
    title: "Get More Clients",
    description: "Trato helps you find, onboard, and manage clients more efficiently.",
    features: [
      "Public profile and custom links to showcase your portfolio",
      "Real-time communication through integrated messaging",
      "Premium client onboarding experience through dedicated portals",
      "Find contacts by phone for payments, conversations, or onboarding",
      "Automated responses and scheduling with Pipeline",
      "Client ratings and referral rewards to grow your business"
    ],
    icon: Users
  },
  {
    value: "taxes",
    title: "Cut on Taxes & Accounting",
    description: "Save money and time on tax preparation and financial management.",
    features: [
      "Receive international payments in one place with lightning billing",
      "Easily allocate contributions toward savings, retirement, and benefits",
      "Automated tax categorization and preparation",
      "Track deductible business expenses",
      "Generate financial reports for tax time",
      "Monitor retirement contributions and tax advantages"
    ],
    icon: Wallet
  },
  {
    value: "time",
    title: "Save Time on Admin Tasks",
    description: "Automate repetitive tasks and streamline your workflow.",
    features: [
      "Keep track of leads with an organized Pipeline",
      "Multiple payment options for your clients through various channels",
      "Organize tasks with tags, filters, and views by client or project",
      "Automated bill management with tracking and categorization",
      "Set rules for payments and expenses",
      "Calendar integration for scheduling and reminders"
    ],
    icon: Calendar
  },
  {
    value: "benefits",
    title: "Enable Retirement & Benefits",
    description: "Access benefits and retirement planning typically unavailable to freelancers.",
    features: [
      "Collect payments in US Dollars from anywhere",
      "Track income and expenses with intuitive financial tools",
      "Automatically route money when project milestones are met",
      "Allocate contributions for retirement and financial goals",
      "Enroll in local benefits through our private marketplace",
      "Monitor investments and track performance over time"
    ],
    icon: Bell
  },
  {
    value: "ai",
    title: "Harness AI Features",
    description: "Let AI assistants handle routine tasks while you focus on creative work.",
    features: [
      "Team of custom AI assistants and automated workflows",
      "Control account actions via text or WhatsApp",
      "Create automated reports and scheduled messages",
      "Convert clients into measurable community with programmatic messaging",
      "AI-powered invoicing and contract generation",
      "Smart task prioritization and scheduling"
    ],
    icon: MessageSquare
  }
]

// Pricing plans data
const pricingPlans = [
  {
    name: "Base Core",
    price: "205.70",
    description: "Essential tools for freelancers just starting out",
    popular: false,
    features: [
      "Budgeting Suite & monthly Automated Reports",
      "Benefits marketplace",
      "Financial Automaton Rules",
      "Basic client management tools",
      "Realtime Payments with local options",
      "Retirement contributions on autopilot",
      "Client portals (limited to 2/mo)",
      "DarDen Local Card access"
    ]
  },
  {
    name: "Basic",
    price: "247",
    description: "Save 10% with more advanced features",
    popular: true,
    features: [
      "All features included in Base Core",
      "Invoicing",
      "Pay by WhatsApp link",
      "Personalized links",
      "Pipeline analytics",
      "Calendar Integration",
      "DarDen Local Card access"
    ]
  },
  {
    name: "Full",
    price: "500",
    description: "Save 16.67% with our most comprehensive package",
    popular: false,
    features: [
      "All Basic features included",
      "Unlimited Client Portals",
      "Tax filings for individuals and entities",
      "Escrow / Retainer Account",
      "Business expensing tools",
      "Accounting reconciliation + AI",
      "Advanced client management",
      "White-labeled options",
      "AI Assistant and automatons"
    ]
  }
]

// Testimonials data
const testimonials = [
  {
    quote: "Trato has transformed how I run my freelance business. I've saved countless hours on admin work and finally have a retirement plan!",
    name: "Sarah Johnson",
    title: "Graphic Designer"
  },
  {
    quote: "The client portal and payment features alone are worth it. My clients love the professional experience, and I get paid faster.",
    name: "Michael Chen",
    title: "Web Developer"
  },
  {
    quote: "As a growing freelance writer, I needed a solution that could scale with me. Trato's AI features have been a game-changer for my productivity.",
    name: "Jennifer Wells",
    title: "Content Writer"
  },
  {
    quote: "After years of struggling with taxes and benefits as a freelancer, Trato has finally given me peace of mind about my financial future.",
    name: "Robert Thompson",
    title: "Marketing Consultant"
  }
]